import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, CheckCircle, XCircle, AlertCircle, Link2, Settings2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { apiRequest } from "../../lib/utils";

const integrationIcons = {
  meta_ads: Globe,
  servall_crm: Link2,
  field_marketing: Settings2,
  other_website: Globe
};

export const IntegrationStatusWidget = () => {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegrationStatus();
  }, []);

  const fetchIntegrationStatus = async () => {
    try {
      const data = await apiRequest("/integrations/status/summary");
      setIntegrations(data.integrations || []);
    } catch (error) {
      console.error("Failed to fetch integration status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "error":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status, isActive) => {
    if (!isActive) return "Inactive";
    switch (status) {
      case "connected":
        return "Connected";
      case "error":
        return "Error";
      default:
        return "Disconnected";
    }
  };

  const getStatusColor = (status, isActive) => {
    if (!isActive) return "text-gray-400";
    switch (status) {
      case "connected":
        return "text-green-600";
      case "error":
        return "text-red-600";
      default:
        return "text-gray-500";
    }
  };

  if (loading) {
    return (
      <Card className="bento-card" data-testid="integration-status-widget">
        <CardContent className="p-5">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeCount = integrations.filter(i => i.connection_status === "connected").length;

  return (
    <Card className="bento-card" data-testid="integration-status-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">Integration Status</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/settings/api-integrations")}
            className="text-xs"
            data-testid="manage-integrations-btn"
          >
            Manage
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="mb-3">
          <p className="text-2xl font-bold font-heading">
            {activeCount} / {integrations.length}
          </p>
          <p className="text-xs text-muted-foreground">Active connections</p>
        </div>
        <div className="space-y-2">
          {integrations.map((integration) => {
            const Icon = integrationIcons[integration.integration_name] || Globe;
            return (
              <div
                key={integration.integration_name}
                className="flex items-center justify-between py-1.5"
                data-testid={`widget-integration-${integration.integration_name}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{integration.display_name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {getStatusIcon(integration.is_active ? integration.connection_status : "inactive")}
                  <span className={`text-xs font-medium ${getStatusColor(integration.connection_status, integration.is_active)}`}>
                    {getStatusText(integration.connection_status, integration.is_active)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default IntegrationStatusWidget;
